// The single source of truth for "this tool call spawns agent work" - the
// rule set behind the AGENTS lanes, the spawn-prompt matching, and the
// execution-window brackets. Every rule here is CORPUS-EVIDENCED: it exists
// because the staging tool-call census (30d, ~377k calls) showed the tool in
// real traffic with the input shape the rule checks. Names alone are never
// enough - a tracker tool called "Task" is not a spawner - so each rule pins
// the shape that distinguishes spawning from housekeeping.
//
// Census notes (2026-08-10), so the next editor knows why the set looks the
// way it does:
//   Agent (3,893)                       Claude Code sub-agents; {description,
//                                       prompt, subagent_type}. "Task" is the
//                                       same tool's legacy name - zero calls
//                                       in 30d, kept for old captures.
//   Workflow (86)                       Claude Code workflow fan-out; spawns
//                                       many agents that never appear as tool
//                                       calls themselves. {script | scriptPath
//                                       | name, args}.
//   spawn_agent (18) / followup_task (5)
//                                       Codex's agent lifecycle; {message} is
//                                       an ENCRYPTED blob, so the spawn's
//                                       content is opaque — but {task_name}
//                                       ("/root/work_o3003") and {agent_type}
//                                       are PLAINTEXT when present, and a
//                                       spawn without one answers with
//                                       {"task_name": …} in its result. That
//                                       handle is the lane's identity: it
//                                       names the child, and send_message /
//                                       interrupt_agent address it via a
//                                       plaintext {target} (5,033 send calls
//                                       carry one). wait_agent/close_agent
//                                       stay collectors, not spawners.
//   mcp__<server>__spawn_task (84)      background-session spawner (CCD);
//                                       {prompt, title, cwd} - readable, but
//                                       the child runs as its OWN session, so
//                                       it never appears among a run's sibling
//                                       traces.
//   transfer_to_* handoffs              zero corpus traffic - deliberately NOT
//                                       a rule yet; add it with evidence.

const clip = (s, n) => {
  const t = String(s || "");
  return t.length > n ? `${t.slice(0, n - 1)}…` : t;
};

const str = (v) => (typeof v === "string" && v.trim() ? v : "");

// A Codex task name is a path ("/root/north_star_guide_20260819_2237"); the
// basename is the readable identity, the full path stays on the lane for
// target matching.
export function taskNameLabel(taskName) {
  const s = str(taskName);
  if (!s) return "";
  return s.replace(/\\/g, "/").split("/").filter(Boolean).pop() || "";
}

/**
 * Classify one tool call as an agent spawn.
 *
 * Returns null for anything that is not a spawn. Otherwise:
 *   flavor      "subagent" (child joins this run) | "fanout" (spawns many,
 *               children invisible as tool calls) | "session" (child runs as
 *               a separate session, never a sibling trace of this run)
 *   label       rail label for the lane
 *   agentType   secondary tag when the input carries one
 *   spawnPrompt the child's opening prompt when readable ("" when the
 *               harness seals it - detection without content)
 *   opaque      true when the spawn content is sealed/encrypted
 *   spawnToken  a sealed Codex spawn's provider-encrypted task token, the
 *               one string its child's opening carries verbatim ("" when
 *               the message is not one)
 */
export function spawnFromToolUse(name, input) {
  const n = String(name || "");
  const arg = input && typeof input === "object" ? input : {};
  if (n === "Agent" || n === "Task") {
    const prompt = str(arg.prompt);
    if (!prompt) return null;
    return {
      flavor: "subagent",
      label: clip(str(arg.description) || str(arg.subagent_type) || n, 44),
      agentType: str(arg.subagent_type),
      spawnPrompt: prompt,
      opaque: false,
    };
  }
  if (n === "spawn_agent" || n === "followup_task") {
    // A follow-up names the child it continues in `target`: the same
    // registered path a spawn's receipt returns, and just as plaintext.
    const taskName = str(arg.task_name) || (n === "followup_task" ? str(arg.target) : "");
    if (!str(arg.message) && !taskName) return null;
    return {
      flavor: "subagent",
      label: clip(
        taskNameLabel(taskName) || (n === "followup_task" ? "follow-up agent (sealed)" : "sub-agent (sealed)"),
        44,
      ),
      agentType: str(arg.agent_type),
      spawnPrompt: "",
      opaque: true,
      taskName: taskName || "",
      spawnToken: SEALED_TOKEN.test(str(arg.message)) ? str(arg.message) : "",
    };
  }
  if (n === "Workflow") {
    const script = str(arg.script);
    const scriptPath = str(arg.scriptPath);
    const wfName = str(arg.name);
    if (!script && !scriptPath && !wfName) return null;
    const base = scriptPath ? scriptPath.replace(/\\/g, "/").split("/").filter(Boolean).pop() : "";
    return {
      flavor: "fanout",
      label: clip(`workflow: ${wfName || base || "inline script"}`, 44),
      agentType: "workflow",
      // The spawn content, best available: an inline script carries the
      // child agents' prompts verbatim, and a named workflow's string
      // args carry the task it fans out - either supports containment
      // matching and the execution-window bracket.
      spawnPrompt: script || str(arg.args),
      opaque: false,
    };
  }
  if (/^mcp__.+__spawn_task$/.test(n)) {
    const prompt = str(arg.prompt);
    if (!prompt) return null;
    return {
      flavor: "session",
      label: clip(str(arg.title) || prompt, 44),
      agentType: "background session",
      spawnPrompt: prompt,
      opaque: false,
    };
  }
  return null;
}

/**
 * True when a ToolCall execution row (name + stringified input) is one of
 * the spawn tools - the execution-window bracket's matching set.
 */
export function isSpawnExecutionName(name) {
  const n = String(name || "");
  return n === "Agent" || n === "Task" || n === "spawn_agent" || n === "followup_task"
    || n === "Workflow" || /^mcp__.+__spawn_task$/.test(n);
}

/**
 * Task notifications embedded in a text block. A background task's
 * completion re-enters the parent conversation as an in-band
 * <task-notification> frame whose <tool-use-id> names the ORIGINAL spawn's
 * tool_use block id - the same id agent lanes key on - so report arrivals
 * join their spawn exactly, from content alone. Tool-catalog prose mentions
 * the tag but never ships a frame carrying ids, so requiring one rejects
 * catalog text. A resumed agent notifies more than once per task id.
 */
export function taskNotificationsIn(text) {
  const s = String(text || "");
  if (!s.includes("<task-notification>")) return [];
  const out = [];
  const span = /<task-notification>([\s\S]*?)<\/task-notification>/g;
  const tag = (body, name) => {
    const m = body.match(new RegExp(`<${name}>([\\s\\S]*?)</${name}>`));
    return m ? m[1].trim() : "";
  };
  let m;
  while ((m = span.exec(s))) {
    const body = m[1];
    const toolUseId = tag(body, "tool-use-id");
    const taskId = tag(body, "task-id");
    if (!toolUseId && !taskId) continue;
    out.push({
      toolUseId,
      taskId,
      status: tag(body, "status"),
      summary: tag(body, "summary"),
      result: tag(body, "result"),
    });
  }
  return out;
}

// A payload no reader can open: the provider-encrypted token itself (Fernet,
// `gAAAAA...`), or the marker older agents wrote in its place.
const SEALED_TOKEN = /^gAAAAA[\w=-]+$/;
const SEALED_PAYLOAD = /^(?:\[task content encrypted|gAAAAA[\w=-]+(?:\s+gAAAAA[\w=-]+)*\s*$)/;

/**
 * A Codex agent message as it reaches a conversation: a user turn framed
 * `Message Type: <TYPE>` / `Task name: <path>` / `Sender: <path>` /
 * `Payload:`, then the payload. What a parent sends a child arrives
 * encrypted; a child's FINAL_ANSWER reaches its parent in plaintext, and that
 * report is the content the two threads share. Returns one entry for a
 * framed text, else none.
 */
export function codexAgentMessagesIn(text) {
  const m = /^\s*Message Type: ([A-Z_]+)\nTask name: (\S+)\nSender: (\S+)\nPayload:\n\n?([\s\S]*)$/.exec(String(text || ""));
  if (!m) return [];
  return [{ type: m[1], taskName: m[2], sender: m[3], payload: m[4], sealed: SEALED_PAYLOAD.test(m[4]) }];
}

/**
 * The async-spawn acknowledgement: a tool_result that confirms the launch
 * and promises a later notification. It is NOT the agent's report - lanes
 * must not present it as the merge point.
 */
export function isAsyncLaunchAck(text) {
  return String(text || "").includes("Async agent launched successfully");
}

/**
 * A Codex spawn's acknowledgement: the tool_result is a bare JSON object
 * carrying the child's task_name. Returns that name, or null — which doubles
 * as the ack test (a spawn result that names the task is a launch receipt,
 * not the child's report).
 */
export function spawnAckTaskName(text) {
  const s = String(text || "").trim();
  if (!s.startsWith("{") || !s.includes("task_name")) return null;
  try {
    const v = JSON.parse(s);
    return str(v?.task_name) || null;
  } catch {
    return null;
  }
}

/**
 * A message INTO a spawned agent's thread: send_message / interrupt_agent
 * addressing a child by its plaintext task-name target. The message body is
 * sealed (encrypted) — the addressing is not, and it is what lets the mark
 * land on the right lane.
 */
export function agentTargetOf(name, input) {
  const n = String(name || "");
  if (n !== "send_message" && n !== "interrupt_agent") return null;
  const target = str(input && typeof input === "object" ? input.target : "");
  if (!target) return null;
  return { kind: n === "send_message" ? "send" : "interrupt", target };
}
